import { get } from "lodash";
import { takeLatest, call, put, all, cancel } from "redux-saga/effects";

import { getEntitySaga } from "./apiSagas.js";
import { operationSaga } from "./operationSagas.js";
import { selectEndpointByTypeSaga } from "./endpointSagas.js";
import { accountSaga } from "./accountSagas.js";

import { splitByRowState } from "../../utils/tableUtils.js";

import { AT_TYPES } from "../../constants/entity.js";
import { ALL_RESOURCES } from "../../constants/schedules.js";
import { OPERATIONS } from "../../constants/operations.js";
import { SCHEDULES, SCHEDULE_ACTIONS } from "../actions/actionConstants.js";

import { createAction } from "../actions/actionUtils.js";
import { operationAction as createOperationAction } from "../reducers/operations/operationActions.js";

import { getSchedule } from "../reducers/schedules/scheduleActions.js";
import { getAccount } from "../reducers/account/accountActions.js";

// -- FIELD MANAGEMENT -- //

const scheduleFields = [
  "name",
  "description",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "startAt",
  "endAt",
  "timezone"
];

const actionFields = ["at", "value", "description"];

function filterEntitiyByFields(entity, fields) {
  return fields.reduce((acc, name) => {
    acc[name] = get(entity, name, undefined);
    return acc;
  }, {});
}

// -- SCHEDULES -- //

export function* schedulesSaga(action) {
  const atId = action.payload["@id"];

  // we will look at how best to deal with individual list operations later
  // sticking with getEntities for now
  if (atId) {
    yield call(getEntitySaga, SCHEDULES, { showErrorModal: true }, action);
  } else {
    const operationAction = createOperationAction(
      SCHEDULES,
      yield call(selectEndpointByTypeSaga, AT_TYPES.SCHEDULE),
      OPERATIONS.LIST,
      action.payload
    );
    yield call(operationSaga, operationAction);
  }
}

export function* scheduleCreateSaga(action) {
  const data = filterEntitiyByFields(action.payload, scheduleFields);

  const createScheduleAction = createOperationAction(
    SCHEDULES,
    yield call(selectEndpointByTypeSaga, AT_TYPES.SCHEDULE),
    OPERATIONS.CREATE,
    data,
    { autoComplete: false }
  );

  try {
    const schedule = yield call(operationSaga, createScheduleAction, true);

    let calls = {};

    const scheduleActions = get(action, "payload.actions", null);
    if (scheduleActions) {
      calls.actions = call(scheduleActionSaga, schedule, scheduleActions);
    }

    const scheduleResources = get(action, "payload.resources", null);
    if (scheduleResources) {
      calls.resources = call(
        scheduleResourcesSaga,
        schedule,
        scheduleResources
      );
    }

    yield all(calls);
    // using direct saga call rather than put so we can be sure it returns
    yield call(accountSaga, undefined, getAccount());
    // whatever happens we have to complete the schedule put or the SPA will hang
    yield put(createAction(SCHEDULES.POST_COMPLETE, schedule));
    // GET the schedule once all updates are complete
    // it's cleaner than trying to update the reducer with all of the various returns that happen here
    yield put(getSchedule(schedule["@id"]));
  } catch (error) {
    console.warn("WARNING scheduleCreateSaga error ::", error);
    // whatever happens we have to complete the schedule put or the SPA will hang
    yield put(createAction(SCHEDULES.POST_COMPLETE));
    yield cancel();
  }
}

export function* scheduleUpdateSaga(action) {
  const entity = action.payload;
  const data = filterEntitiyByFields(entity, scheduleFields);

  const operationAction = createOperationAction(
    SCHEDULES,
    entity,
    OPERATIONS.UPDATE,
    data,
    { autoComplete: false }
  );

  let calls = {};

  calls.update = call(operationSaga, operationAction, true);

  if (entity.actions) {
    calls.actions = call(scheduleActionSaga, entity, entity.actions);
  }

  if (entity.resources) {
    calls.resources = call(scheduleResourcesSaga, entity, entity.resources);
  }

  try {
    yield all(calls);
    // whatever happens we have to complete the schedule put or the SPA will hang
    yield put(createAction(SCHEDULES.PUT_COMPLETE));
    // GET the schedule once all updates are complete
    // it's cleaner than trying to update the reducer with all of the various returns that happen here
    yield put(getSchedule(entity["@id"]));
  } catch (error) {
    console.warn("WARNING scheduleUpdateSaga error ::", error);
    // whatever happens we have to complete the schedule put or the SPA will hang
    yield put(createAction(SCHEDULES.PUT_COMPLETE));
    yield cancel();
  }
}

// -- SCHEDULE RESOURCES -- //

export function* scheduleResourcesSaga(schedule, resources) {
  if (!schedule || !resources || resources.length < 1) {
    return null;
  }

  const splitResources = splitByRowState(resources, "resources");

  let calls = {};

  ALL_RESOURCES.forEach(values => {
    // may be able to remove this later once endpoint can acecept objects as well as IRI
    const resources = splitResources.new.reduce((acc, resource) => {
      if (resource["@type"] === values["@type"]) {
        acc.push(resource["@id"]);
      }
      return acc;
    }, []);

    if (resources.length > 0) {
      const createScheduleResourcesAction = createOperationAction(
        SCHEDULES,
        schedule,
        values.operations.add,
        { [values.key]: resources },
        { autoComplete: false }
      );

      calls[`new-${values.key}`] = call(
        operationSaga,
        createScheduleResourcesAction
      );
    }
  });

  for (let i = 0; i < splitResources.deleted.length; i++) {
    const entity = splitResources.deleted[i];
    const scheduleResourceOperation = createOperationAction(
      SCHEDULE_ACTIONS,
      entity,
      OPERATIONS.DELETE,
      undefined,
      { autoComplete: false }
    );
    calls[entity["@id"]] = call(operationSaga, scheduleResourceOperation);
  }

  return yield all(calls);
}

// -- SCHEDULE ACTIONS -- //

export function* scheduleActionSaga(schedule, actions) {
  if (!schedule || !actions) return null;

  const splitActions = splitByRowState(actions, "actions");

  let calls = {};

  if (splitActions.new.length > 0) {
    const createScheduleActionsAction = createOperationAction(
      SCHEDULE_ACTIONS,
      schedule,
      OPERATIONS.ADD_ACTION,
      { actions: splitActions.new },
      { autoComplete: false }
    );

    calls["new-actions"] = call(operationSaga, createScheduleActionsAction);
  }

  for (let i = 0; i < splitActions.edited.length; i++) {
    const entity = splitActions.edited[i];
    const scheduleActionOperation = createOperationAction(
      SCHEDULE_ACTIONS,
      entity,
      OPERATIONS.UPDATE,
      filterEntitiyByFields(entity, actionFields),
      { autoComplete: false }
    );
    calls[entity["@id"]] = call(operationSaga, scheduleActionOperation);
  }

  for (let i = 0; i < splitActions.deleted.length; i++) {
    const entity = splitActions.deleted[i];
    const scheduleActionOperation = createOperationAction(
      SCHEDULE_ACTIONS,
      entity,
      OPERATIONS.DELETE,
      undefined,
      { autoComplete: false }
    );
    calls[entity["@id"]] = call(operationSaga, scheduleActionOperation);
  }

  return yield all(calls);
}

// -- WATCHERS -- //

export function* watchScheduleSagas() {
  yield takeLatest(SCHEDULES.GET, schedulesSaga);
  yield takeLatest(SCHEDULES.POST, scheduleCreateSaga);
  yield takeLatest(SCHEDULES.PUT, scheduleUpdateSaga);
}
